/* The Lazy Butcher — analytics events for GTM / GA4.
 *
 * Every event is a dataLayer push; GTM forwards them to GA4 with one
 * "all custom events" GA4 Event tag (see docs/ANALYTICS.md for the event list,
 * parameters, key events, and the GTM import file). Nothing here can break the
 * page: every handler is wrapped, and without GTM dataLayer is a plain array.
 *
 * global.js owns: generate_lead, sign_up, form_error, popup_*, view_item,
 * add_to_cart, add_to_calendar, share, event_builder_open.
 * main-cart.liquid owns: view_cart, remove_from_cart, begin_checkout.
 * This file owns: every link click, form_start/form_abandon, scroll depth, menu/event
 * filters, gallery, content views, search, and 404s.
 */
(function () {
  'use strict';

  function push(name, params) {
    try {
      if (window.lbTrackEvent) return window.lbTrackEvent(name, params);
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(Object.assign({ event: name }, params || {}));
    } catch (e) { /* never break the page */ }
  }

  const clean = (t) => (t || '').replace(/\s+/g, ' ').trim().slice(0, 100);

  function linkText(a) {
    return clean(a.textContent) || clean(a.getAttribute('aria-label')) ||
      clean((a.querySelector('img[alt]') || {}).alt) || clean(a.title);
  }

  // Where on the page a click happened: header, mobile menu, footer, or the
  // section it sits in (by its heading), so "Order online" in the hero and in
  // the footer are distinguishable.
  function linkLocation(el) {
    if (el.closest('[data-nav-drawer], .nav-drawer, .mobile-nav')) return 'mobile_menu';
    if (el.closest('header, .site-header')) return 'header';
    if (el.closest('footer, .site-footer')) return 'footer';
    if (el.closest('[role="dialog"], .modal, [data-onload-popup], [data-exit-intent-popup]')) return 'popup';
    const section = el.closest('.shopify-section, section');
    if (section) {
      const heading = section.querySelector('h1, h2, h3');
      if (heading) return clean(heading.textContent).slice(0, 60);
      if (section.id) return section.id.replace(/^shopify-section-(template--\d+__)?/, '');
    }
    return 'body';
  }

  const RULES = [
    { event: 'click_directions', test: (u) => /(^|\.)maps\.app\.goo\.gl$|(^|\.)goo\.gl$|maps\.apple\.com$/.test(u.hostname) || /google\.[a-z.]+\/maps/.test(u.href) },
    { event: 'click_order_online', test: (u) => /spoton\.com$|toasttab\.com$|doordash\.com$|ubereats\.com$|grubhub\.com$/.test(u.hostname), extra: (u) => ({ order_platform: u.hostname.replace(/^www\./, '') }) },
    { event: 'click_gift_cards', test: (u) => /securetree\.com$/.test(u.hostname) || /gift-?card/i.test(u.pathname) },
    { event: 'click_reservation', test: (u) => /opentable\.com$|resy\.com$|exploretock\.com$|sevenrooms\.com$/.test(u.hostname) },
    { event: 'click_jobs', test: (u) => /^jobs\.lazybutcher\.com$/.test(u.hostname) },
    { event: 'click_reviews', test: (u) => /^share\.google$|^g\.page$/.test(u.hostname) || /search\.google\.com\/local\/writereview|google\.[a-z.]+\/search.*lazy\+butcher/i.test(u.href) },
    { event: 'click_social', test: (u) => /(^|\.)(facebook|instagram|tiktok|x|twitter|youtube|linkedin|pinterest)\.com$/.test(u.hostname), extra: (u) => ({ social_network: u.hostname.replace(/^www\./, '').split('.')[0] }) },
    { event: 'click_black_hat', test: (u) => /blackhatcattle\.co$/.test(u.hostname) },
  ];
  const FILE_RE = /\.(pdf|docx?|xlsx?|pptx?|csv|zip|jpe?g|png|webp)$/i;

  function onClick(e) {
    const a = e.target.closest && e.target.closest('a[href]');
    if (a) return classifyLink(a);
    const btn = e.target.closest && e.target.closest('button, [role="button"]');
    if (btn) classifyButton(btn);
  }

  function classifyLink(a) {
    const href = a.getAttribute('href') || '';
    const base = { link_text: linkText(a), link_location: linkLocation(a) };

    if (/^tel:/i.test(href)) {
      return push('click_to_call', Object.assign(base, { phone_number: href.replace(/^tel:/i, '') }));
    }
    if (/^sms:/i.test(href)) {
      return push('click_text', Object.assign(base, { phone_number: href.replace(/^sms:/i, '').split('?')[0] }));
    }
    if (/^mailto:/i.test(href)) {
      return push('click_email', Object.assign(base, { email_address: href.replace(/^mailto:/i, '').split('?')[0] }));
    }
    if (href.startsWith('#') || /^javascript:/i.test(href)) return;
    // Calendar links are tracked as add_to_calendar in global.js.
    if (a.hasAttribute('data-event-calendar-link') || /\.ics($|\?)/i.test(href)) return;

    let url;
    try { url = new URL(a.href, window.location.href); } catch (err) { return; }
    base.link_url = url.href.slice(0, 250);
    if (a.hasAttribute('data-promo-link')) base.campaign_name = a.dataset.promoCampaign;

    if (FILE_RE.test(url.pathname)) {
      const fileName = decodeURIComponent(url.pathname.split('/').pop());
      const ext = (fileName.split('.').pop() || '').toLowerCase();
      push('file_download', Object.assign({}, base, { file_name: fileName, file_extension: ext }));
      if (ext === 'pdf' && /menu/i.test(fileName + ' ' + base.link_text)) {
        push('download_menu', Object.assign({}, base, { menu_name: fileName.replace(/\.pdf$/i, '') }));
      }
      return;
    }

    const external = url.hostname !== window.location.hostname && !/(^|\.)lazybutcher\.com$/.test(url.hostname) || /^jobs\./.test(url.hostname);
    if (external) {
      const rule = RULES.find((r) => { try { return r.test(url); } catch (err) { return false; } });
      if (rule) push(rule.event, Object.assign(base, rule.extra ? rule.extra(url) : {}));
      push('click_outbound', Object.assign({}, base, { link_domain: url.hostname }));
      return;
    }

    // Internal links: navigation vs. calls to action.
    const loc = base.link_location;
    if (loc === 'header' || loc === 'mobile_menu' || loc === 'footer') {
      push('nav_click', Object.assign(base, { nav_location: loc }));
    } else if (a.matches('.btn, [class*="btn--"], [data-track-cta]')) {
      push('cta_click', Object.assign(base, { cta_text: base.link_text }));
    } else if (a.matches('.event-card__link, .press-card__link, .blog-card__link, .blog-featured__link')) {
      push('select_content', Object.assign(base, { content_type: a.className.split('__')[0], item_id: url.pathname }));
    }
  }

  function classifyButton(btn) {
    if (btn.matches('[data-menu-filter]')) {
      return push('select_content', { content_type: 'menu_category', item_id: btn.dataset.menuFilter || 'all', link_location: linkLocation(btn) });
    }
    if (btn.matches('[data-event-month]')) {
      return push('select_content', { content_type: 'event_month', item_id: btn.dataset.eventMonth || 'all' });
    }
    if (btn.matches('[data-nav-toggle], .nav-toggle, [aria-controls*="nav" i]')) {
      return push('menu_open', { link_location: 'header' });
    }
    if (btn.matches('[data-track-cta]')) {
      push('cta_click', { cta_text: clean(btn.textContent), link_location: linkLocation(btn) });
    }
  }

  /* Form funnel: form_start → generate_lead (success, in global.js) or
     form_abandon (started, then the page was left or its popup/modal closed
     without a successful send). */
  const LEAD_FORMS = 'form[data-lb-form]';
  const formName = (form) => form.dataset.formName || form.dataset.recoveryName || form.id ||
    (form.dataset.endpoint || '').split('/').pop() || 'form';

  function filledFields(form) {
    return Array.from(form.elements).filter((el) => {
      if (!el.name || el.name === 'lb_hp' || el.type === 'hidden' || el.type === 'submit') return false;
      if (el.type === 'checkbox' || el.type === 'radio') return false;
      return (el.value || '').trim().length > 0;
    }).length;
  }

  function initFormFunnel() {
    document.addEventListener('input', (e) => {
      const form = e.target.closest && e.target.closest(LEAD_FORMS);
      if (!form || e.target.name === 'lb_hp' || e.target.type === 'hidden') return;
      form.__lbLastField = e.target.name || e.target.id || '';
      if (form.__lbStarted) return;
      form.__lbStarted = true;
      push('form_start', {
        form_name: formName(form),
        form_endpoint: form.dataset.endpoint || '',
        link_location: linkLocation(form),
      });
    }, true);
    document.addEventListener('submit', (e) => {
      const form = e.target.closest && e.target.closest(LEAD_FORMS);
      if (form) form.__lbSubmitAttempted = true;
    }, true);
    document.addEventListener('lb:form-success', (e) => {
      const form = e.target.closest && e.target.closest(LEAD_FORMS);
      if (form) form.__lbSucceeded = true;
    }, true);

    function abandon(form, reason) {
      if (!form.__lbStarted || form.__lbSucceeded || form.__lbAbandoned) return;
      form.__lbAbandoned = true;
      push('form_abandon', {
        form_name: formName(form),
        form_endpoint: form.dataset.endpoint || '',
        abandon_reason: reason,
        last_field: form.__lbLastField || '',
        fields_completed: filledFields(form),
        submit_attempted: form.__lbSubmitAttempted ? 'yes' : 'no',
      });
    }
    // Closing the popup/modal the form lives in.
    ['lb:popup-dismissed', 'lb:modal-closed'].forEach((type) => {
      document.addEventListener(type, (e) => {
        e.target.querySelectorAll && e.target.querySelectorAll(LEAD_FORMS).forEach((f) => abandon(f, 'closed_popup'));
      });
    });
    // Leaving the page (navigation, tab close, app switch on mobile).
    const onLeave = () => document.querySelectorAll(LEAD_FORMS).forEach((f) => abandon(f, 'left_page'));
    window.addEventListener('pagehide', onLeave);
    document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'hidden') onLeave(); });
  }

  // Scroll depth at 25/50/75/90% (GA4's built-in scroll only reports 90%).
  function initScrollDepth() {
    const marks = [25, 50, 75, 90];
    const hit = new Set();
    let ticking = false;
    function check() {
      ticking = false;
      const doc = document.documentElement;
      const max = doc.scrollHeight - window.innerHeight;
      if (max <= 0) return;
      const pct = (window.scrollY / max) * 100;
      marks.forEach((m) => {
        if (pct >= m && !hit.has(m)) { hit.add(m); push('scroll_depth', { percent_scrolled: m }); }
      });
      if (hit.size === marks.length) window.removeEventListener('scroll', onScroll);
    }
    function onScroll() { if (!ticking) { ticking = true; requestAnimationFrame(check); } }
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  // Content views declared in Liquid: <div data-track-view="view_menu" data-track-menu_name="Restaurant">
  function initViews() {
    document.querySelectorAll('[data-track-view]').forEach((el) => {
      const params = {};
      Object.keys(el.dataset).forEach((k) => {
        if (k.startsWith('track') && k !== 'trackView') {
          const key = k.slice(5).replace(/^[A-Z]/, (c) => c.toLowerCase()).replace(/[A-Z]/g, (c) => '_' + c.toLowerCase());
          params[key] = el.dataset[k];
        }
      });
      push(el.dataset.trackView, params);
    });
  }

  // Gallery / lightbox opens.
  function initGallery() {
    document.addEventListener('click', (e) => {
      const img = e.target.closest && e.target.closest('[data-lightbox-gallery] img, [data-menu-gallery] img, .gallery img');
      if (!img) return;
      push('select_content', { content_type: 'gallery_image', item_id: clean(img.alt).slice(0, 80) || img.currentSrc.split('/').pop().split('?')[0] });
    }, true);
  }

  // Cow share (and any product) option changes.
  function initVariantSelect() {
    document.addEventListener('change', (e) => {
      const input = e.target;
      const form = input.closest && input.closest('#product-form, .cs-product__form, form[action*="/cart/add"]');
      if (!form || !(input.type === 'radio' || input.tagName === 'SELECT')) return;
      push('select_item', {
        item_list_name: document.title.split(' | ')[0],
        option_name: clean(input.name.replace(/^properties\[|\]$/g, '').replace(/^options\[|\]$/g, '')),
        option_value: clean(input.value),
      });
    }, true);
  }

  function init() {
    document.addEventListener('click', onClick, true);
    initFormFunnel();
    initScrollDepth();
    initViews();
    initGallery();
    initVariantSelect();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
